import React, { useState } from 'react';
import {
    Avatar,
    Box,
    Flex,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Spinner,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import CustomDrawer from 'components/ui/Drawer';
import EpisodeCreateModal from 'components/layouts/patients/EpisodeCreateModal';
import { usePatients } from 'hooks/usePatients';
import { Patient } from 'types/models';

interface NewEpisodeDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export const NewEpisodeDrawer: React.FC<NewEpisodeDrawerProps> = ({ isOpen, onClose }) => {
    const [search,           setSearch]           = useState('');
    const [selectedPatient,  setSelectedPatient]  = useState<Patient | null>(null);
    const [openFullModal,    setOpenFullModal]     = useState(false);

    const { data: patients = [], isLoading } = usePatients();

    const labelColor = useColorModeValue('gray.700', 'gray.200');
    const mutedColor = useColorModeValue('gray.400', 'gray.500');
    const hoverBg    = useColorModeValue('gray.50',  'gray.700');
    const rowBorder  = useColorModeValue('gray.100', 'gray.700');

    const filtered = (patients as Patient[]).filter((p: Patient) => {
        const q = search.toLowerCase();
        return (
            p.nombres.toLowerCase().includes(q) ||
            p.apellidos.toLowerCase().includes(q) ||
            p.cedula.toLowerCase().includes(q)
        );
    });

    const resetAll = () => {
        setSearch('');
        setSelectedPatient(null);
        setOpenFullModal(false);
    };

    const handleClose = () => {
        resetAll();
        onClose();
    };

    const handleSelectPatient = (patient: Patient) => {
        setSelectedPatient(patient);
        setOpenFullModal(true);
        onClose();
    };

    const handleModalClose = () => {
        resetAll();
    };

    const drawerTitle = (
        <Flex align="center" gap="10px">
            <Icon viewBox="0 0 24 24" w="20px" h="20px" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                <path d="M9 14h6" />
                <path d="M12 17v-6" />
            </Icon>
            Nuevo Episodio
        </Flex>
    );

    return (
        <>
            <CustomDrawer
                isOpen={isOpen}
                onClose={handleClose}
                title={drawerTitle}
                size="sm"
                placement="right"
                borderRadius="10px"
                borderRadiusSides="left"
                width="400px"
                layout="list"
                contentPaddingX="20px"
                contentPaddingY="20px"
            >
                <Text fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase" letterSpacing="0.08em" mb="12px">
                    Seleccionar Paciente
                </Text>
                <InputGroup size="sm" mb="16px">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={MdSearch} color={mutedColor} />
                    </InputLeftElement>
                    <Input
                        borderRadius="8px"
                        placeholder="Buscar por nombre o cédula..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                </InputGroup>

                {isLoading ? (
                    <Flex justify="center" py="40px"><Spinner size="md" color="brand.500" /></Flex>
                ) : filtered.length === 0 ? (
                    <Flex justify="center" py="40px">
                        <Text fontSize="sm" color={mutedColor}>
                            {search ? 'Sin resultados' : 'No hay pacientes registrados'}
                        </Text>
                    </Flex>
                ) : (
                    <Box>
                        {filtered.map((patient, i) => (
                            <Flex
                                key={patient.id}
                                align="center"
                                gap="12px"
                                px="12px"
                                py="10px"
                                cursor="pointer"
                                borderRadius="8px"
                                _hover={{ bg: hoverBg }}
                                transition="background 0.12s"
                                onClick={() => handleSelectPatient(patient)}
                                borderBottom="1px solid"
                                borderColor={i < filtered.length - 1 ? rowBorder : 'transparent'}
                            >
                                <Avatar size="sm" name={`${patient.nombres} ${patient.apellidos}`} bg="brand.500" color="white" fontSize="xs" />
                                <Box flex={1} minW={0}>
                                    <Text fontSize="sm" fontWeight="600" color={labelColor} isTruncated>
                                        {patient.nombres} {patient.apellidos}
                                    </Text>
                                    <Text fontSize="xs" color={mutedColor}>{patient.cedula}</Text>
                                </Box>
                                <Icon viewBox="0 0 24 24" w="16px" h="16px" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" color={mutedColor}>
                                    <path d="m9 18 6-6-6-6" />
                                </Icon>
                            </Flex>
                        ))}
                    </Box>
                )}
            </CustomDrawer>

            {openFullModal && selectedPatient && (
                <EpisodeCreateModal
                    isOpen={true}
                    onClose={handleModalClose}
                    patientId={selectedPatient.id}
                />
            )}
        </>
    );
};

export default NewEpisodeDrawer;
